import { MerchantValidationError } from "../error";
import * as Fields from "../fields";
import { FieldSet } from "../fieldset";
import { Merchant } from "../merchant";
import {
  Acquirer,
  AveTimeToShip,
  CurrenciesAvailable,
  ECommercePlatform,
  Industry,
  InterestedInXBT,
  SalesChannel,
  SubIndustry,
  TotalAnnualSales,
} from "../types";

export type StoreDetailsFieldSetValueMap = {
  acquirer: Acquirer;
  artisan: string;
  aveTimeToShip: AveTimeToShip;
  contactlessPayment: string;
  currenciesAvailable: CurrenciesAvailable;
  ddsRestricted: string;
  facebookUrl: string;
  industry: Industry;
  interestedInXBT: InterestedInXBT;
  multiTerminal: string;
  nonFamilyEmployee: string;
  numOfStores: number;
  platform: ECommercePlatform;
  preOrders: string;
  reseller: string;
  salesChannel: SalesChannel;
  selfFulfillment: string;
  selfServeInStore: boolean;
  storeName: string;
  subIndustry: SubIndustry;
  totalAnnualSales: TotalAnnualSales;
  totalAnnualSalesInStore: number;
  website: string;
  websiteNotLive: string;
};

export type StoreDetailsFieldSetValueMapper<T> = {
  [K in keyof StoreDetailsFieldSetValueMap]: T;
};

export class StoreDetailsFieldSet extends FieldSet<StoreDetailsFieldSetValueMap> {
  static fromJson(
    StoreDetails: StoreDetailsFieldSet,
    {
      acquirer,
      artisan,
      aveTimeToShip,
      contactlessPayment,
      currenciesAvailable,
      ddsRestricted,
      facebookUrl,
      industry,
      interestedInXBT,
      multiTerminal,
      nonFamilyEmployee,
      numOfStores,
      platform,
      preOrders,
      reseller,
      salesChannel,
      selfFulfillment,
      storeName,
      subIndustry,
      totalAnnualSales,
      totalAnnualSalesInStore,
      website,
      websiteNotLive,
    }: Partial<StoreDetailsFieldSetValueMap>
  ): StoreDetailsFieldSet {
    StoreDetails.acquirer.value = acquirer;
    StoreDetails.artisan.value = artisan;
    StoreDetails.aveTimeToShip.value = aveTimeToShip;
    StoreDetails.contactlessPayment.value = contactlessPayment;
    StoreDetails.currenciesAvailable.value = currenciesAvailable;
    StoreDetails.ddsRestricted.value = ddsRestricted;
    StoreDetails.facebookUrl.value = facebookUrl;
    StoreDetails.industry.value = industry;
    StoreDetails.interestedInXBT.value = interestedInXBT;
    StoreDetails.nonFamilyEmployee.value = nonFamilyEmployee;
    StoreDetails.multiTerminal.value = multiTerminal;
    StoreDetails.numOfStores.value = numOfStores;
    StoreDetails.platform.value = platform;
    StoreDetails.preOrders.value = preOrders;
    StoreDetails.reseller.value = reseller;
    StoreDetails.salesChannel.value = salesChannel;
    StoreDetails.selfFulfillment.value = selfFulfillment;
    StoreDetails.storeName.value = storeName;
    StoreDetails.subIndustry.value = subIndustry;
    StoreDetails.totalAnnualSales.value = totalAnnualSales;
    StoreDetails.totalAnnualSalesInStore.value = totalAnnualSalesInStore;
    StoreDetails.website.value = website;
    StoreDetails.websiteNotLive.value = websiteNotLive;

    return StoreDetails;
  }

  public acquirer: Fields.AcquirerField;
  public artisan: Fields.ArtisanField;
  public aveTimeToShip: Fields.AveTimeToShipField;
  public contactlessPayment: Fields.ContactlessPaymentField;
  public currenciesAvailable: Fields.CurrenciesAvailableField;
  public ddsRestricted: Fields.DDSRestrictedField;
  public facebookUrl: Fields.FacebookUrlField;
  public industry: Fields.IndustryField;
  public interestedInXBT: Fields.InterestedInXbtField;
  public nonFamilyEmployee: Fields.NonFamilyEmployeeField;
  public multiTerminal: Fields.MultiTerminalField;
  public numOfStores: Fields.NumberOfStoresField;
  public platform: Fields.PlatformField;
  public preOrders: Fields.PreOrdersField;
  public reseller: Fields.ResellerField;
  public salesChannel: Fields.SalesChannelField;
  public selfFulfillment: Fields.SelfFulfillmentField;
  public selfServeInStore: Fields.SelfServeInStoreField;
  public storeName: Fields.StoreNameField;
  public subIndustry: Fields.SubIndustryField;
  public totalAnnualSales: Fields.TotalAnnualSalesField;
  public totalAnnualSalesInStore: Fields.TotalAnnualSalesInStoreField;
  public website: Fields.WebsiteField;
  public websiteNotLive: Fields.WebsiteNotLiveField;

  constructor(model: Merchant) {
    super();

    this.fieldSetName = "StoreDetailsFieldSet";

    this.acquirer = new Fields.AcquirerField(model, this, {});
    this.artisan = new Fields.ArtisanField(model);
    this.aveTimeToShip = new Fields.AveTimeToShipField(model);
    this.contactlessPayment = new Fields.ContactlessPaymentField(model, this);
    this.currenciesAvailable = new Fields.CurrenciesAvailableField(model);
    this.ddsRestricted = new Fields.DDSRestrictedField(model);
    this.facebookUrl = new Fields.FacebookUrlField(model);
    this.industry = new Fields.IndustryField(model, this);
    this.interestedInXBT = new Fields.InterestedInXbtField(model);
    this.nonFamilyEmployee = new Fields.NonFamilyEmployeeField(model);
    this.multiTerminal = new Fields.MultiTerminalField(model);
    this.numOfStores = new Fields.NumberOfStoresField(model, this);
    this.platform = new Fields.PlatformField(model, this);
    this.preOrders = new Fields.PreOrdersField(model);
    this.reseller = new Fields.ResellerField(model);
    this.salesChannel = new Fields.SalesChannelField(model, this);
    this.selfFulfillment = new Fields.SelfFulfillmentField(model);
    this.selfServeInStore = new Fields.SelfServeInStoreField(model);
    this.storeName = new Fields.StoreNameField(model, this);
    this.subIndustry = new Fields.SubIndustryField(model, this);
    this.totalAnnualSales = new Fields.TotalAnnualSalesField(model, this);
    this.totalAnnualSalesInStore = new Fields.TotalAnnualSalesInStoreField(
      model,
      this
    );
    this.website = new Fields.WebsiteField(model, this);
    this.websiteNotLive = new Fields.WebsiteNotLiveField(model);

    this.fields = [
      this.acquirer,
      this.artisan,
      this.aveTimeToShip,
      this.contactlessPayment,
      this.currenciesAvailable,
      this.ddsRestricted,
      this.facebookUrl,
      this.industry,
      this.interestedInXBT,
      this.nonFamilyEmployee,
      this.multiTerminal,
      this.numOfStores,
      this.platform,
      this.preOrders,
      this.reseller,
      this.salesChannel,
      this.selfFulfillment,
      this.selfServeInStore,
      this.storeName,
      this.subIndustry,
      this.totalAnnualSales,
      this.totalAnnualSalesInStore,
      this.website,
      this.websiteNotLive,
    ];
  }

  validate({ stringify = true }: { stringify: boolean }): void {
    const Errors = new MerchantValidationError();

    let errorCount = 0;
    for (const field of this.fields) {
      try {
        field.validate();
      } catch (ex) {
        errorCount++;
        if (Errors.fieldErrorMap.storeDetails) {
          Errors.fieldErrorMap.storeDetails[
            field.propertyName as keyof StoreDetailsFieldSetValueMap
          ] = { error: stringify ? ex.message : ex };
        }
      }
    }

    if (errorCount) {
      throw Errors.fieldErrorMap;
    }
  }
}
